import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CredentialItem } from '@dreamai/types';
import api from '../../api';
import { ReactComponent as CardVisaIcon } from '../../icons/card-visa.svg';
import { ReactComponent as CardAmexIcon } from '../../icons/card-amex.svg';
import { ReactComponent as CardMastercardIcon } from '../../icons/card-mastercard.svg';
import { ReactComponent as CardOtherIcon } from '../../icons/card-other.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash-outline.svg';
import { ReactComponent as LockIcon } from '../../icons/lock-outline.svg';
import AddPaymentCardModal from './sidebar/AddPaymentCardModal';
import SecureCredentialModal from './sidebar/SecureCredentialModal';
import {
  CredentialRow,
  isSecretFieldName,
  makeCredentialRow,
} from './sidebar/CredentialFieldEditor';
import './credential-card.scss';

/**
 * Fields we expect the Bitwarden vault to return for a card credential.
 * This is the same shape that `storeAndVaultCredential` writes in the BE,
 * and the keys we display + feed back into `AddPaymentCardModal` as prefill
 * when the user hits Edit.
 */
const CARD_FIELD_DISPLAY_ORDER: string[] = [
  'cardholderName',
  'cardNumber',
  'expiryDate',
  'cvv',
  'billingAddressLine1',
  'billingAddressLine2',
  'billingCity',
  'billingPostcode',
  'billingCountry',
];

const CARD_FIELD_LABELS: Record<string, string> = {
  cardholderName: 'Cardholder name',
  cardNumber: 'Card number',
  expiryDate: 'Expiry',
  cvv: 'CVV',
  billingAddressLine1: 'Billing address',
  billingAddressLine2: 'Address line 2',
  billingCity: 'City',
  billingPostcode: 'Postcode',
  billingCountry: 'Country',
};

// Fields we mask behind Show/Hide. Username/notes-style fields aren't
// sensitive; card number + CVV are. Expiry and cardholder name are OK
// to show.
const CARD_FIELD_ALWAYS_VISIBLE = new Set([
  'cardholderName',
  'expiryDate',
  'billingAddressLine1',
  'billingAddressLine2',
  'billingCity',
  'billingPostcode',
  'billingCountry',
]);

function formatCardNumberForDisplay(digits: string): string {
  return (digits || '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ');
}

interface CredentialCardProps {
  credential: CredentialItem;
  onInvalidate: (credentialId: string) => Promise<void>;
  /**
   * Called after a successful in-place edit so the parent can re-fetch the
   * memories list (which carries `updatedAt` and other fields the card
   * displays). Optional — the card will still work without it, just won't
   * automatically refresh sibling cards.
   */
  onUpdated?: () => void;
}

/**
 * Format a `createdBySource` enum value into a short, user-friendly
 * provenance line shown at the bottom of the card.
 *
 * We deliberately do NOT surface the task ID — a truncated UUID like
 * "#e022defc" means nothing to the user. When we have a better source
 * for "the user's framing of the task" (e.g. the originating sub-thread
 * title), we can extend this to link back to the task.
 *
 * - `human_task` / `alert_human` — Dream's ops team created the credential
 *   while completing a task on the user's behalf.
 * - `browser_agent` — autonomous agent created it during a signup flow.
 * - `user_form` — the user submitted it themselves via a clarifying-question
 *   form. We don't render anything for this case to avoid noise.
 * - `admin` — created by an internal tool/migration; surfaced for transparency.
 */
function formatProvenance(
  source: CredentialItem['createdBySource'] | undefined | null,
): string | null {
  switch (source) {
    case 'human_task':
      return 'Created by Dream during a task';
    case 'alert_human':
      return 'Created by Dream during a browser session';
    case 'browser_agent':
      return 'Created by Dream during a signup';
    case 'admin':
      return 'Created by Dream support';
    case 'user_form':
    case null:
    case undefined:
    default:
      return null;
  }
}

function getCardIcon(network: string | null | undefined) {
  switch (network) {
    case 'visa':
      return <CardVisaIcon />;
    case 'mastercard':
      return <CardMastercardIcon />;
    case 'amex':
      return <CardAmexIcon />;
    default:
      return <CardOtherIcon />;
  }
}

const CredentialCard: FC<CredentialCardProps> = ({
  credential,
  onInvalidate,
  onUpdated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [invalidating, setInvalidating] = useState(false);

  // Reveal state — we fetch the vaulted fields lazily on first click and
  // keep them in component state until the user clicks Hide. Reveals are
  // audit-logged on the backend so we don't try to be clever about caching
  // on the client.
  const [revealedFields, setRevealedFields] = useState<Record<
    string,
    string
  > | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [visibleFieldKeys, setVisibleFieldKeys] = useState<Set<string>>(
    new Set(),
  );
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Edit-mode state — we open the unified SecureCredentialModal in update
  // mode rather than rendering a separate inline form, so the user sees the
  // exact same UX (dynamic rows, secret toggle, add/remove fields, generate
  // password) regardless of whether they're creating a new credential or
  // editing an existing one. The rows are seeded from the revealed vault
  // contents at edit-open time so the modal shows what's currently stored.
  const [editing, setEditing] = useState(false);
  const [editInitialRows, setEditInitialRows] = useState<CredentialRow[]>([]);
  const [editInitialLabel, setEditInitialLabel] = useState<string>('');
  const [editInitialSite, setEditInitialSite] = useState<string>('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const isCard = credential.credentialType === 'card';
  const networkClass = isCard
    ? `card-network-${credential.cardNetwork || 'other'}`
    : '';

  const handleInvalidate = async () => {
    if (invalidating) return;
    setInvalidating(true);
    try {
      await onInvalidate(credential.id);
    } finally {
      setInvalidating(false);
    }
  };

  const fetchRevealedFields = useCallback(async (): Promise<Record<
    string,
    string
  > | null> => {
    const token = await getAccessTokenSilently();
    const response = await api.get(
      `/memories/credentials/${credential.id}/reveal`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.data?.success && response.data.fields) {
      return response.data.fields as Record<string, string>;
    }
    throw new Error(response.data?.error || 'Failed to reveal credential');
  }, [credential.id, getAccessTokenSilently]);

  const handleReveal = useCallback(async () => {
    if (revealLoading) return;
    if (revealedFields) {
      // Toggling closed.
      setRevealedFields(null);
      setVisibleFieldKeys(new Set());
      setRevealError(null);
      return;
    }
    setRevealLoading(true);
    setRevealError(null);
    try {
      const fields = await fetchRevealedFields();
      setRevealedFields(fields);
    } catch (err: any) {
      setRevealError(err?.message || 'Failed to reveal credential');
    } finally {
      setRevealLoading(false);
    }
  }, [revealLoading, revealedFields, fetchRevealedFields]);

  const toggleFieldVisibility = useCallback((key: string) => {
    setVisibleFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleCopy = useCallback(async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  }, []);

  const startEdit = useCallback(async () => {
    setEditError(null);
    setEditInitialLabel(credential.label);
    setEditInitialSite(credential.site || '');
    // We need the current vault fields to pre-populate the form. If they're
    // already revealed we use them directly; otherwise we fetch them.
    let fields = revealedFields;
    if (!fields) {
      try {
        fields = await fetchRevealedFields();
      } catch (err: any) {
        setEditError(err?.message || 'Failed to load credential for editing');
        return;
      }
    }
    // Convert the flat `Record<string, string>` from the vault into editor
    // rows. Secret-ness is inferred from the key name so the round-trip
    // (vault → edit → save) keeps the original `isSecret` setting without
    // the user having to retag every row. The user can still toggle Secret
    // off/on per row if the inferred value is wrong.
    if (!fields) {
      // fetchRevealedFields above either returned a non-null map or
      // threw; this branch is unreachable but keeps the TS narrower happy.
      return;
    }
    const rows: CredentialRow[] = Object.entries(fields).map(([k, v]) =>
      makeCredentialRow({
        name: k,
        value: v,
        isSecret: isSecretFieldName(k),
      }),
    );
    setEditInitialRows(rows);
    setEditing(true);
  }, [credential.label, credential.site, revealedFields, fetchRevealedFields]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setEditError(null);
    setEditInitialRows([]);
  }, []);

  const submitEdit = useCallback(
    async (
      values: Record<string, string>,
      opts?: {
        label?: string;
        site?: string;
        secretFieldKeys?: string[];
      },
    ) => {
      if (editSubmitting) return;
      setEditError(null);
      setEditSubmitting(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await api.patch(
          `/memories/credentials/${credential.id}`,
          {
            fields: values,
            label: opts?.label,
            site:
              opts?.site && opts.site.trim().length > 0
                ? opts.site.trim()
                : null,
            secretFieldKeys: opts?.secretFieldKeys,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data?.success) {
          setEditing(false);
          setEditInitialRows([]);
          // Refresh revealed values so the panel shows the new password
          // immediately if the user opens it again.
          setRevealedFields(null);
          setVisibleFieldKeys(new Set());
          if (onUpdated) onUpdated();
        } else {
          setEditError(response.data?.error || 'Failed to update credential');
          window.alert(response.data?.error || 'Failed to update credential');
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || 'Failed to update credential';
        setEditError(msg);
        window.alert(msg);
      } finally {
        setEditSubmitting(false);
      }
    },
    [editSubmitting, credential.id, getAccessTokenSilently, onUpdated],
  );

  // Reset transient state when switching between credentials in the list.
  useEffect(() => {
    setRevealedFields(null);
    setRevealError(null);
    setRevealLoading(false);
    setVisibleFieldKeys(new Set());
    setEditing(false);
    setEditInitialRows([]);
    setEditError(null);
    setEditInitialLabel(credential.label);
    setEditInitialSite(credential.site || '');
  }, [credential.id, credential.label, credential.site]);

  const formattedDate = new Date(credential.createdAt).toLocaleDateString(
    undefined,
    { year: 'numeric', month: 'short', day: 'numeric' },
  );

  // Show "Updated" line only when the row has actually been modified since
  // creation (the trigger sets updated_at = created_at on insert, but the
  // database stores both with the same precision; allow a 2s grace window
  // for clock skew between the insert and the trigger fire).
  const updatedAtMs = credential.updatedAt
    ? new Date(credential.updatedAt).getTime()
    : null;
  const createdAtMs = new Date(credential.createdAt).getTime();
  const wasUpdated = updatedAtMs !== null && updatedAtMs - createdAtMs > 2000;
  const formattedUpdatedDate = wasUpdated
    ? new Date(credential.updatedAt as string).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  const provenanceLine = formatProvenance(credential.createdBySource);

  // Card-type credentials get Reveal / Edit / Remove. Reveal uses the
  // shared `/memories/credentials/:id/reveal` endpoint (which now accepts
  // cards); Edit re-uses `AddPaymentCardModal` in its "replace" mode with
  // the revealed fields pre-filled so the user sees what was stored and
  // can tweak individual values.
  if (isCard) {
    const revealedFieldEntries = revealedFields
      ? CARD_FIELD_DISPLAY_ORDER.filter(
          (k) =>
            revealedFields[k] !== undefined &&
            revealedFields[k] !== null &&
            revealedFields[k] !== '',
        ).map((k) => [k, revealedFields[k]] as [string, string])
      : [];

    const handleCardEditClick = async () => {
      setRevealError(null);
      // Ensure we have the vault fields before opening the modal — the
      // edit flow is only useful if we can show what's already stored
      // (including CVV, which is otherwise not recoverable).
      if (!revealedFields) {
        setRevealLoading(true);
        try {
          const fields = await fetchRevealedFields();
          setRevealedFields(fields);
        } catch (err: any) {
          setRevealError(err?.message || 'Failed to load card for editing');
          setRevealLoading(false);
          return;
        }
        setRevealLoading(false);
      }
      setEditing(true);
    };

    const handleCardEditSuccess = () => {
      setEditing(false);
      setRevealedFields(null);
      setVisibleFieldKeys(new Set());
      if (onUpdated) onUpdated();
    };

    return (
      <div className={`credential-card ${networkClass}`}>
        <div className='credential-card-top'>
          <div className='credential-card-icon'>
            {getCardIcon(credential.cardNetwork)}
          </div>
          <div className='credential-card-details'>
            <span className='credential-card-label'>{credential.label}</span>
            {credential.cardLastFour && (
              <span className='credential-card-last-four'>
                •••• •••• •••• {credential.cardLastFour}
              </span>
            )}
            {credential.purpose && (
              <span className='credential-card-purpose'>
                {credential.purpose}
              </span>
            )}
          </div>
        </div>

        {revealError && (
          <div className='credential-card-error'>{revealError}</div>
        )}

        {revealedFields && (
          <div className='credential-card-fields'>
            {revealedFieldEntries.map(([key, value]) => {
              const isAlwaysVisible = CARD_FIELD_ALWAYS_VISIBLE.has(key);
              const isVisible = isAlwaysVisible || visibleFieldKeys.has(key);
              const displayValue =
                key === 'cardNumber' && isVisible
                  ? formatCardNumberForDisplay(value)
                  : value;
              return (
                <div key={key} className='credential-card-field-row'>
                  <span className='credential-card-field-label'>
                    {CARD_FIELD_LABELS[key] || key}
                  </span>
                  <div className='credential-card-field-value-row'>
                    <span className='credential-card-field-value mono'>
                      {isVisible ? displayValue : '••••••••••••'}
                    </span>
                    {!isAlwaysVisible && (
                      <button
                        type='button'
                        className='credential-card-inline-btn'
                        onClick={() => toggleFieldVisibility(key)}
                      >
                        {isVisible ? 'Hide' : 'Show'}
                      </button>
                    )}
                    <button
                      type='button'
                      className={`credential-card-inline-btn ${
                        copiedKey === key ? 'copied' : ''
                      }`}
                      onClick={() =>
                        handleCopy(
                          key,
                          key === 'cardNumber'
                            ? value.replace(/\D/g, '')
                            : value,
                        )
                      }
                    >
                      {copiedKey === key ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className='credential-card-bottom'>
          <div className='credential-card-dates'>
            <span className='credential-card-date'>Stored {formattedDate}</span>
            {formattedUpdatedDate && (
              <span className='credential-card-date'>
                Updated {formattedUpdatedDate}
              </span>
            )}
          </div>
          <div className='credential-card-actions'>
            <button
              type='button'
              className='credential-card-action-btn'
              onClick={handleReveal}
              disabled={revealLoading}
            >
              {revealLoading ? 'Loading…' : revealedFields ? 'Hide' : 'Reveal'}
            </button>
            <button
              type='button'
              className='credential-card-action-btn'
              onClick={handleCardEditClick}
              disabled={revealLoading}
            >
              Edit
            </button>
            <button
              className='credential-card-remove'
              onClick={handleInvalidate}
              disabled={invalidating}
            >
              <TrashIcon />
              {invalidating ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>

        <AddPaymentCardModal
          show={editing}
          onClose={() => setEditing(false)}
          onSuccess={handleCardEditSuccess}
          existingCredentialId={credential.id}
          existingCardLabel={credential.label}
          prefillFields={
            revealedFields
              ? { ...revealedFields, nickname: credential.label }
              : undefined
          }
        />
      </div>
    );
  }

  // Login-type / generic credentials — full reveal + edit + provenance.
  return (
    <div className={`credential-card ${networkClass}`}>
      <div className='credential-card-top'>
        <div className='credential-card-icon'>
          <LockIcon />
        </div>
        <div className='credential-card-details'>
          <span className='credential-card-label'>{credential.label}</span>
          {credential.site ? (
            <span className='credential-card-site'>{credential.site}</span>
          ) : (
            <span className='credential-card-type'>
              {credential.credentialType}
            </span>
          )}
          {credential.purpose && (
            <span className='credential-card-purpose'>
              {credential.purpose}
            </span>
          )}
          {provenanceLine && (
            <span className='credential-card-provenance'>{provenanceLine}</span>
          )}
        </div>
      </div>

      {revealError && (
        <div className='credential-card-error'>{revealError}</div>
      )}

      {revealedFields && !editing && (
        <div className='credential-card-fields'>
          {Object.entries(revealedFields).map(([key, value]) => {
            const isVisible = visibleFieldKeys.has(key);
            return (
              <div key={key} className='credential-card-field-row'>
                <span className='credential-card-field-label'>{key}</span>
                <div className='credential-card-field-value-row'>
                  <span className='credential-card-field-value mono'>
                    {isVisible || key === 'username' || key === 'notes'
                      ? value
                      : '••••••••••••'}
                  </span>
                  {key !== 'username' && key !== 'notes' && (
                    <button
                      type='button'
                      className='credential-card-inline-btn'
                      onClick={() => toggleFieldVisibility(key)}
                    >
                      {isVisible ? 'Hide' : 'Show'}
                    </button>
                  )}
                  <button
                    type='button'
                    className={`credential-card-inline-btn ${
                      copiedKey === key ? 'copied' : ''
                    }`}
                    onClick={() => handleCopy(key, value)}
                  >
                    {copiedKey === key ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        // The modal is the unified editor used by every credential-entry
        // surface. We supply the existing credential id (→ update banner +
        // replacesCredentialId echo), the existing label/site as initial
        // input values (user can rename), the existing fields as initial
        // rows (user can rename / retoggle / add / remove), and the
        // password-generator helper so users can rotate a leaked password
        // without leaving the editor.
        <SecureCredentialModal
          title='Edit sign-in details'
          existingCredentialId={credential.id}
          initialLabel={editInitialLabel}
          initialSite={editInitialSite}
          initialRows={editInitialRows}
          helpers={{ generatePassword: true, copyValue: true }}
          onSubmit={submitEdit}
          onClose={cancelEdit}
          submitting={editSubmitting}
        />
      )}

      <div className='credential-card-bottom'>
        <div className='credential-card-dates'>
          <span className='credential-card-date'>Stored {formattedDate}</span>
          {formattedUpdatedDate && (
            <span className='credential-card-date'>
              Updated {formattedUpdatedDate}
            </span>
          )}
        </div>
        <div className='credential-card-actions'>
          {!editing && (
            <>
              <button
                type='button'
                className='credential-card-action-btn'
                onClick={handleReveal}
                disabled={revealLoading}
              >
                {revealLoading
                  ? 'Loading…'
                  : revealedFields
                    ? 'Hide'
                    : 'Reveal'}
              </button>
              <button
                type='button'
                className='credential-card-action-btn'
                onClick={startEdit}
              >
                Edit
              </button>
              <button
                className='credential-card-remove'
                onClick={handleInvalidate}
                disabled={invalidating}
              >
                <TrashIcon />
                {invalidating ? 'Removing…' : 'Remove'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialCard;
